import {type KeyboardEvent, useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {Controller, useFormContext, UseFormGetValues} from 'react-hook-form';
import {TFunction} from 'i18next';
import {
  PATTERNS,
  isActiveFormField,
  POLICE_CODES,
  COUNTRY_CODES,
  QUERY_CACHE_KEYS,
  GUEST_FORM_NAMES,
  SelectOption,
} from '@chekin/core';
import {useReservation} from 'context/reservation';
import {useGuestGroup, useFirstGuestMustBeAdult} from 'hooks';
import {useSchema} from 'context/guest';
import {i18n} from '../../configs/i18n/i18n';
import {
  EXTRA_FORM_NAMES,
  FormTypes,
  getIsLeaderGuest,
  IV_FIELDS,
  SIGNATURE_FIELDS,
} from 'utils/guestForm';
import {createGetRequiredOrOptionalFieldLabel} from 'utils/getRequiredOrOptionalFieldLabel';
import {getIsSelectFieldType, getResidenceCityQueryKey} from './utils/common';
import {
  handleChangeResidenceCountry,
  handleChangeResidenceProvince,
} from './utils/handlers';
import {sortFormNamesByOrder} from './utils/sortFormNamesByOrder';
import {type FormFieldsMapper} from './types';
import {
  getDocNumberPattern,
  maxBirthPlaceAddressLength,
  maxLength,
  maxNamesInputLength,
  minLength,
  validateChipCard,
  validateBirthDate,
  validateDocNumberByDocumentType,
  validateExpirationDate,
  validateIssueDate,
  validateResidencePhoneNumber,
  validateGuestMustBeAdult,
  validatePostalCode,
  validateDocNumberUniqueness,
  validateSelectedDocumentType,
  getDocumentSupportNumberInvalidMessage,
} from 'pages/AddPersonalDataForm/utils';
import {
  AirbnbInput as Input,
  AirbnbSelect as Select,
  DatePicker,
  PhoneField,
} from '@chekinapp/ui';
import {FormFieldWrapper} from 'styled/common';
import {API, Validators} from '@chekin/api';
import {CountriesSelect, useFetchLocations} from '@chekin/shared';
import {PaginatedSearchableSelect} from './PaginatedSearchableSelect';

const VARIANT = 'airbnb' as const;

const getNameValidationErrorText = (country: string, policeType = '') => {
  if (
    country === COUNTRY_CODES.spain &&
    [POLICE_CODES.hospedajes, POLICE_CODES.mossos, POLICE_CODES.ertzaintza].includes(
      policeType,
    )
  ) {
    return i18n.t('guest_names_validation_text.ses_and_mossos_spain');
  } else if (
    [COUNTRY_CODES.spain, COUNTRY_CODES.uae, COUNTRY_CODES.czech].includes(country)
  ) {
    return i18n.t('guest_names_validation_text.only_letters');
  } else {
    return i18n.t('guest_names_validation_text.rest');
  }
};

const filterGenderOptions = (options?: SelectOption[]): SelectOption[] | undefined => {
  return options?.filter(opt => opt?.value !== 'N');
};

const getNoOptionsMessageForDocTypeField =
  (getValues: UseFormGetValues<FormTypes>, t: TFunction) => () => {
    const isNationalityFilled = Boolean(getValues()[GUEST_FORM_NAMES.nationality]?.value);

    return !isNationalityFilled ? t('please_fill_nationality_before') : undefined;
  };

const getPhoneFieldLabel = (defaultLabel: string, t: TFunction, policeType?: string) => {
  if (policeType && [POLICE_CODES.uhh, POLICE_CODES.uhh2].includes(policeType)) {
    return t('residence_country_phone');
  }

  return defaultLabel;
};

const getRecipientCodeLabel = (defaultLabel: string, recipientCodeType?: string) => {
  return recipientCodeType || defaultLabel;
};

type Props = {
  housingCountry: string;
  guestId?: string;
  isDisabledField?: boolean;
};

export const useAirbnbFieldsMapper = ({
  guestId,
  isDisabledField,
  housingCountry,
}: Props) => {
  const {formFields, setHasPhone, isGuestSchemaLoading} = useSchema();
  const {phoneCodesAsOptions} = useFetchLocations();
  const {guestGroup} = useGuestGroup();
  const {firstGuestMustBeAdult: shouldFirstGuestBeAdult, guestMustBeAdult} =
    useFirstGuestMustBeAdult();

  const {register, control, formState, setFocus, setValue, getValues, watch} =
    useFormContext<FormTypes>();
  const {t} = useTranslation();
  const getRequiredOrOptionalFieldLabel = useMemo(
    () => createGetRequiredOrOptionalFieldLabel(t),
    [t],
  );

  const defaultSecondPhoneDetails = getValues()?.[EXTRA_FORM_NAMES.secondPhoneDetails];

  const {data: reservation, housing} = useReservation();

  const errors = useMemo(() => {
    return formState.errors;
  }, [formState]);
  const policeType = housing?.police_account_type;
  const isPoliceActivated = housing?.is_police_registration_enabled;

  const names = useMemo(() => {
    const names: GUEST_FORM_NAMES[] = [];
    if (Object.values(formFields).length > 0) {
      Object.entries(formFields).forEach(([name, value]) => {
        if (
          value.is_active &&
          !IV_FIELDS.includes(name) &&
          !SIGNATURE_FIELDS.includes(name as GUEST_FORM_NAMES)
        ) {
          names.push(name as GUEST_FORM_NAMES);
        }
      });
      return sortFormNamesByOrder(names, formFields);
    } else {
      return [];
    }
  }, [formFields]);

  const getFormFieldDestinations = useMemo(() => {
    return names.map((formName, index) => {
      const destination = index < names.length - 1 ? names[index + 1] : '';
      return {
        state: formName,
        destination,
      };
    });
  }, [names]);

  const getNextField = useCallback(
    (nextFieldName: GUEST_FORM_NAMES) => {
      return getFormFieldDestinations.find(item => item.state === nextFieldName);
    },
    [getFormFieldDestinations],
  );

  const handleKeyDown = useCallback(
    (nextFieldName: GUEST_FORM_NAMES) => (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        const next = getNextField(nextFieldName);
        next && setFocus(next?.destination);
      }
    },
    [setFocus, getNextField],
  );

  const documentType = watch(GUEST_FORM_NAMES.document_type)?.value;
  const residenceCountry = watch(GUEST_FORM_NAMES.residence_country)?.value;
  const residenceProvince = watch(GUEST_FORM_NAMES.residence_province)?.value;
  const arrivedFromDistrict = watch(GUEST_FORM_NAMES.arrived_from_district)?.value;
  const nextDestinationDistrict = watch(
    GUEST_FORM_NAMES.next_destination_district,
  )?.value;
  const birthPlaceCountry = watch(GUEST_FORM_NAMES.birth_place_country)?.value;
  const recipientCodeType = watch(GUEST_FORM_NAMES.recipient_code_type)?.label;

  return useMemo<FormFieldsMapper>(
    () => ({
      [GUEST_FORM_NAMES.name]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            variant={VARIANT}
            label={getRequiredOrOptionalFieldLabel(t('name'), required)}
            {...register(GUEST_FORM_NAMES.name, {
              required,
              pattern: {
                value: format,
                message: getNameValidationErrorText(housingCountry, policeType),
              },
              maxLength: {
                value: maxNamesInputLength,
                message: t('max_length', {length: maxNamesInputLength}),
              },
            })}
            error={errors[GUEST_FORM_NAMES.name]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
            onKeyDown={handleKeyDown(GUEST_FORM_NAMES.name)}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.surname]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            variant={VARIANT}
            label={getRequiredOrOptionalFieldLabel(t('surname'), required)}
            {...register(GUEST_FORM_NAMES.surname, {
              required,
              pattern: {
                value: format,
                message: getNameValidationErrorText(housingCountry, policeType),
              },
              maxLength: {
                value: maxNamesInputLength,
                message: t('max_length', {length: maxNamesInputLength}),
              },
            })}
            error={errors[GUEST_FORM_NAMES.surname]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
            onKeyDown={handleKeyDown(GUEST_FORM_NAMES.surname)}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.second_surname]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            variant={VARIANT}
            label={getRequiredOrOptionalFieldLabel(t('second_surname'), required)}
            {...register(GUEST_FORM_NAMES.second_surname, {
              required,
              pattern: {
                value: format,
                message: getNameValidationErrorText(housingCountry, policeType),
              },
              maxLength: {
                value: maxNamesInputLength,
                message: t('max_length', {length: maxNamesInputLength}),
              },
            })}
            error={errors[GUEST_FORM_NAMES.second_surname]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
            onKeyDown={handleKeyDown(GUEST_FORM_NAMES.second_surname)}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.nationality]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.nationality}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <CountriesSelect
                variant={VARIANT}
                label={getRequiredOrOptionalFieldLabel(t('nationality'), required)}
                disabled={isDisabledField}
                onKeyDown={handleKeyDown(GUEST_FORM_NAMES.nationality)}
                error={error?.message}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.birth_place_country]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.birth_place_country}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <CountriesSelect
                variant={VARIANT}
                label={getRequiredOrOptionalFieldLabel(
                  t('birth_place_country'),
                  required,
                )}
                placeholder={`${t('select')}...`}
                disabled={isDisabledField}
                error={error?.message}
                onKeyDown={handleKeyDown(GUEST_FORM_NAMES.birth_place_country)}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.birth_place_city]: ({required, format}) => {
        const isSelectField = getIsSelectFieldType(
          formFields?.[GUEST_FORM_NAMES.birth_place_city]?.type,
        );
        if (isSelectField) {
          return (
            <FormFieldWrapper>
              <Controller
                name={GUEST_FORM_NAMES.birth_place_city}
                rules={{required}}
                control={control}
                render={({field: {value, ...restField}, fieldState: {error}}) => (
                  <PaginatedSearchableSelect
                    variant={VARIANT}
                    label={getRequiredOrOptionalFieldLabel(t('city_of_birth'), required)}
                    placeholder={`${t('select')}...`}
                    resetKey={birthPlaceCountry}
                    queryKey={[QUERY_CACHE_KEYS.paginatedCitiesList, birthPlaceCountry]}
                    queryFn={({page, searchQuery}) =>
                      API.locations.getCitiesList({
                        key: QUERY_CACHE_KEYS.paginatedCitiesList,
                        page,
                        searchQuery,
                        countryCode: birthPlaceCountry || '',
                      })
                    }
                    disabled={isDisabledField}
                    value={(value as SelectOption) ?? null}
                    error={error?.message}
                    {...restField}
                  />
                )}
              />
            </FormFieldWrapper>
          );
        }
        return (
          <FormFieldWrapper>
            <Input
              variant={VARIANT}
              label={getRequiredOrOptionalFieldLabel(t('city_of_birth'), required)}
              {...register(GUEST_FORM_NAMES.birth_place_city, {
                required,
                pattern: {
                  value: format,
                  message: t('cant_contain_number_and_symbols'),
                },
                maxLength: {
                  value: maxNamesInputLength,
                  message: t('max_length', {length: maxNamesInputLength}),
                },
              })}
              error={errors[GUEST_FORM_NAMES.birth_place_city]?.message}
              disabled={isDisabledField}
              autoCorrect="off"
              spellCheck={false}
              onKeyDown={handleKeyDown(GUEST_FORM_NAMES.birth_place_city)}
            />
          </FormFieldWrapper>
        );
      },
      [GUEST_FORM_NAMES.birth_place_address]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            variant={VARIANT}
            label={getRequiredOrOptionalFieldLabel(t('birth_place_address'), required)}
            {...register(GUEST_FORM_NAMES.birth_place_address, {
              required,
              pattern: {
                value: format,
                message: t('cant_contain_number_and_symbols'),
              },
              maxLength: {
                value: maxBirthPlaceAddressLength,
                message: t('max_length', {length: maxBirthPlaceAddressLength}),
              },
            })}
            error={errors[GUEST_FORM_NAMES.birth_place_address]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
            onKeyDown={handleKeyDown(GUEST_FORM_NAMES.birth_place_address)}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.tax_exemption]: ({
        required,
        options = formFields?.[GUEST_FORM_NAMES.tax_exemption]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.tax_exemption}
            rules={{required}}
            control={control}
            render={({field: {value, onChange, name}, fieldState: {error}}) => (
              <Select
                variant={VARIANT}
                label={getRequiredOrOptionalFieldLabel(t('tax_exemption'), required)}
                placeholder={`${t('select')}...`}
                options={(options ?? []) as SelectOption[]}
                value={value as unknown as SelectOption}
                onChange={onChange}
                name={name}
                error={error?.message}
                disabled={isDisabledField}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.email]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            variant={VARIANT}
            label={getRequiredOrOptionalFieldLabel(t('email'), required)}
            {...register(GUEST_FORM_NAMES.email, {
              required,
              pattern: {
                value: format,
                message: t('invalid_email'),
              },
            })}
            inputMode="email"
            autoCorrect="off"
            spellCheck={false}
            autoCapitalize="none"
            error={errors[GUEST_FORM_NAMES.email]?.message as string}
            disabled={isDisabledField}
            onKeyDown={handleKeyDown(GUEST_FORM_NAMES.email)}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.phone]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.phone}
            rules={{
              required,
              validate: {
                length: validateResidencePhoneNumber(t, policeType),
                full: Validators.phoneValidateService,
              },
            }}
            control={control}
            render={({field: {value, onChange, name}, fieldState: {error}}) => (
              <PhoneField
                variant={VARIANT}
                label={getRequiredOrOptionalFieldLabel(
                  getPhoneFieldLabel(t('phone_number'), t, policeType),
                  required,
                )}
                options={phoneCodesAsOptions}
                value={{
                  code: value?.code ?? '',
                  number: value?.number ?? '',
                }}
                onChange={details => {
                  onChange(details);
                  setHasPhone?.(!!details.number);
                }}
                name={name}
                error={error?.message}
                disabled={isDisabledField}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.second_phone]: ({required, format}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.second_phone}
            rules={{
              required,
              pattern: {
                value: format,
                message: t('invalid_phone_number'),
              },
              maxLength: {
                value: 16,
                message: t('invalid_phone_number'),
              },
              minLength: {
                value: 12,
                message: t('invalid_phone_number'),
              },
            }}
            control={control}
            render={({field: {value, onChange, name}, fieldState: {error}}) => (
              <PhoneField
                variant={VARIANT}
                label={getRequiredOrOptionalFieldLabel(t('uae_phone_number'), required)}
                options={phoneCodesAsOptions.filter(opt => opt.value === '+971')}
                value={{
                  code: value?.code ?? '+971',
                  number: value?.number ?? defaultSecondPhoneDetails?.number ?? '',
                }}
                onChange={details => onChange({...details, code: '+971'})}
                name={name}
                error={error?.message}
                disabled={isDisabledField}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.purpose_of_stay]: ({
        required,
        options = formFields?.[GUEST_FORM_NAMES.purpose_of_stay]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.purpose_of_stay}
            rules={{required}}
            control={control}
            render={({field: {value, onChange, name}, fieldState: {error}}) => (
              <Select
                variant={VARIANT}
                label={getRequiredOrOptionalFieldLabel(t('purpose_of_stay'), required)}
                placeholder={`${t('select')}...`}
                options={(options ?? []) as SelectOption[]}
                value={value as unknown as SelectOption}
                onChange={onChange}
                name={name}
                error={error?.message}
                disabled={isDisabledField || isGuestSchemaLoading}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.document_type]: ({
        required,
        options = formFields?.[GUEST_FORM_NAMES.document_type]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.document_type}
            rules={{
              required,
              validate: {
                validOption: validateSelectedDocumentType(
                  formFields?.[GUEST_FORM_NAMES.document_type]?.options,
                ),
              },
            }}
            control={control}
            render={({field: {value, onChange, name}, fieldState: {error}}) => {
              const noOptionsMessage = getNoOptionsMessageForDocTypeField(getValues, t);
              return (
                <Select
                  variant={VARIANT}
                  label={getRequiredOrOptionalFieldLabel(t('document_type'), required)}
                  placeholder={`${t('select')}...`}
                  options={(options ?? []) as SelectOption[]}
                  value={value as unknown as SelectOption}
                  onChange={onChange}
                  name={name}
                  error={error?.message}
                  disabled={isDisabledField}
                  noOptionsMessage={noOptionsMessage}
                />
              );
            }}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.document_number]: ({required}) => (
        <FormFieldWrapper>
          <Input
            variant={VARIANT}
            label={getRequiredOrOptionalFieldLabel(t('document_number'), required)}
            {...register(GUEST_FORM_NAMES.document_number, {
              required,
              pattern: {
                value: getDocNumberPattern(housingCountry, isPoliceActivated),
                message: t('only_basic_letters_are_allowed'),
              },
              maxLength: {
                value: maxLength,
                message: t('max_length', {length: maxLength}),
              },
              minLength: {
                value: minLength,
                message: t('min_length', {length: minLength}),
              },
              validate: {
                byDocumentType: value =>
                  validateDocNumberByDocumentType(value, documentType),
                uniqueness: validateDocNumberUniqueness(guestGroup, guestId),
              },
            })}
            error={errors[GUEST_FORM_NAMES.document_number]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
            onKeyDown={handleKeyDown(GUEST_FORM_NAMES.document_number)}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.document_issue_date]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.document_issue_date}
            rules={{
              required,
              validate: value => {
                if (!value) return true;
                const birthDate = getValues()[GUEST_FORM_NAMES.birth_date];
                const expirationDate =
                  getValues()[GUEST_FORM_NAMES.document_expiration_date];
                return validateIssueDate(value, birthDate, expirationDate, documentType);
              },
            }}
            control={control}
            render={({field: {value, onChange, name}, fieldState: {error}}) => (
              <DatePicker
                variant={VARIANT}
                label={getRequiredOrOptionalFieldLabel(t('date_of_issue'), required)}
                value={value as Date | null}
                onChange={onChange}
                name={name}
                error={error?.message}
                disabled={isDisabledField}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.document_expiration_date]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.document_expiration_date}
            rules={{
              required,
              validate: value => {
                if (!value) return true;

                const birthDate = getValues()[GUEST_FORM_NAMES.birth_date];
                const issueDate = getValues()[GUEST_FORM_NAMES.document_issue_date];
                return validateExpirationDate({
                  expirationDate: value,
                  birthDate,
                  issueDate,
                  checkInDate: reservation?.check_in_date,
                });
              },
            }}
            control={control}
            render={({field: {value, onChange, name}, fieldState: {error}}) => (
              <DatePicker
                variant={VARIANT}
                label={getRequiredOrOptionalFieldLabel(
                  t('document_expiration_date'),
                  required,
                )}
                value={value as Date | null}
                onChange={onChange}
                name={name}
                error={error?.message}
                disabled={isDisabledField}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.birth_date]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.birth_date}
            rules={{
              required,
              validate: {
                validateGuestMustBeAdult: value => {
                  if (shouldFirstGuestBeAdult || guestMustBeAdult) {
                    const validationType = getIsLeaderGuest() ? 'leader' : 'common';
                    const isSpainHousing = housingCountry === COUNTRY_CODES.spain;

                    return validateGuestMustBeAdult({
                      birthDate: value,
                      validationType,
                      isSpainHousing,
                    });
                  }
                  return true;
                },
                common: value => {
                  const isDocumentIssueDateActive = isActiveFormField(
                    formFields[GUEST_FORM_NAMES.document_issue_date],
                  );
                  const docDateOfIssue =
                    getValues()[GUEST_FORM_NAMES.document_issue_date];
                  const compareDates =
                    isDocumentIssueDateActive && Boolean(docDateOfIssue);

                  if (!value || !compareDates) {
                    return true;
                  }

                  return validateBirthDate(value, docDateOfIssue);
                },
              },
            }}
            control={control}
            render={({field: {value, onChange, name}, fieldState: {error}}) => (
              <DatePicker
                variant={VARIANT}
                label={getRequiredOrOptionalFieldLabel(t('birth_date'), required)}
                value={value as Date | null}
                onChange={onChange}
                name={name}
                error={error?.message}
                disabled={isDisabledField}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.gender]: ({
        required,
        options = formFields?.[GUEST_FORM_NAMES.gender]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.gender}
            rules={{required}}
            control={control}
            render={({field: {value, onChange, name}, fieldState: {error}}) => (
              <Select
                variant={VARIANT}
                label={getRequiredOrOptionalFieldLabel(t('sex'), required)}
                placeholder={`${t('select')}...`}
                options={(filterGenderOptions(options) ?? []) as SelectOption[]}
                value={value as unknown as SelectOption}
                onChange={onChange}
                name={name}
                error={error?.message}
                disabled={isDisabledField}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.citizenship]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.citizenship}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <CountriesSelect
                variant={VARIANT}
                label={getRequiredOrOptionalFieldLabel(t('citizenship'), required)}
                placeholder={`${t('select')}...`}
                disabled={isDisabledField}
                error={error?.message}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.document_expedition_country]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.document_expedition_country}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <CountriesSelect
                variant={VARIANT}
                label={getRequiredOrOptionalFieldLabel(t('country_of_issue'), required)}
                placeholder={`${t('select')}...`}
                disabled={isDisabledField}
                error={error?.message}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.document_expedition_city]: ({required, format}) => {
        const isSelectField = getIsSelectFieldType(
          formFields?.[GUEST_FORM_NAMES.document_expedition_city]?.type,
        );
        if (isSelectField) {
          return (
            <FormFieldWrapper>
              <Controller
                name={GUEST_FORM_NAMES.document_expedition_city}
                rules={{required}}
                control={control}
                render={({field: {value, ...restField}, fieldState: {error}}) => (
                  <PaginatedSearchableSelect
                    variant={VARIANT}
                    label={getRequiredOrOptionalFieldLabel(t('city_of_issue'), required)}
                    placeholder={`${t('select')}...`}
                    resetKey={housingCountry}
                    queryKey={[QUERY_CACHE_KEYS.paginatedCitiesList, housingCountry]}
                    queryFn={({page, searchQuery}) =>
                      API.locations.getCitiesList({
                        key: QUERY_CACHE_KEYS.paginatedCitiesList,
                        page,
                        searchQuery,
                        countryCode: housingCountry,
                      })
                    }
                    disabled={isDisabledField}
                    value={(value as SelectOption) ?? null}
                    error={error?.message}
                    {...restField}
                  />
                )}
              />
            </FormFieldWrapper>
          );
        }
        return (
          <FormFieldWrapper>
            <Input
              variant={VARIANT}
              label={getRequiredOrOptionalFieldLabel(t('city_of_issue'), required)}
              {...register(GUEST_FORM_NAMES.document_expedition_city, {
                required,
                pattern: {
                  value: format,
                  message: t('cant_contain_number_and_symbols'),
                },
                maxLength: {
                  value: maxNamesInputLength,
                  message: t('max_length', {length: maxNamesInputLength}),
                },
              })}
              disabled={isDisabledField}
              autoCorrect="off"
              spellCheck={false}
            />
          </FormFieldWrapper>
        );
      },
      [GUEST_FORM_NAMES.full_tourist_tax]: ({required}) => (
        <FormFieldWrapper>
          <Input
            variant={VARIANT}
            {...register(GUEST_FORM_NAMES.full_tourist_tax, {required})}
            label={getRequiredOrOptionalFieldLabel(t('full_tourist_tax'), required)}
            inputMode="numeric"
            error={errors[GUEST_FORM_NAMES.full_tourist_tax]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.residence_country]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.residence_country}
            rules={{required}}
            control={control}
            render={({field: {onChange, ...field}, fieldState: {error}}) => (
              <CountriesSelect
                variant={VARIANT}
                label={getRequiredOrOptionalFieldLabel(t('residence_country'), required)}
                onChange={(newValue, actionMeta) => {
                  handleChangeResidenceCountry({
                    newValue,
                    formFields,
                    currentValue: residenceCountry,
                    onReset: name => setValue(name, null),
                  });

                  onChange(newValue, actionMeta);
                }}
                placeholder={`${t('select')}...`}
                disabled={isDisabledField}
                error={error?.message}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.residence_community]: ({
        required,
        options = formFields?.[GUEST_FORM_NAMES.residence_community]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.residence_community}
            rules={{required}}
            control={control}
            render={({field: {value, onChange, name}, fieldState: {error}}) => (
              <Select
                variant={VARIANT}
                label={getRequiredOrOptionalFieldLabel(t('residence_state'), required)}
                placeholder={`${t('select')}...`}
                options={(options ?? []) as SelectOption[]}
                value={value as unknown as SelectOption}
                onChange={onChange}
                name={name}
                disabled={isDisabledField}
                error={error?.message}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.residence_province]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.residence_province}
            rules={{required}}
            control={control}
            render={({field: {onChange, ...field}, fieldState: {error}}) => (
              <PaginatedSearchableSelect
                variant={VARIANT}
                label={getRequiredOrOptionalFieldLabel(t('residence_province'), required)}
                onChange={newValue => {
                  handleChangeResidenceProvince({
                    newValue,
                    formFields,
                    currentValue: residenceProvince,
                    onReset: name => setValue(name, null),
                  });
                  onChange(newValue);
                }}
                placeholder={`${t('select')}...`}
                resetKey={housingCountry}
                queryKey={[QUERY_CACHE_KEYS.paginatedProvincesList, housingCountry]}
                queryFn={({page, searchQuery}) =>
                  API.locations.getProvincesList({
                    key: QUERY_CACHE_KEYS.paginatedProvincesList,
                    page,
                    searchQuery,
                    countryCode: housingCountry,
                  })
                }
                disabled={isDisabledField}
                error={error?.message}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.residence_city]: ({required, format}) => {
        const isSelectField = getIsSelectFieldType(
          formFields?.[GUEST_FORM_NAMES.residence_city]?.type,
        );

        if (isSelectField) {
          const isSpain = residenceCountry === COUNTRY_CODES.spain;
          const url = formFields?.[GUEST_FORM_NAMES.residence_city]?.options_url;
          const label = isSpain ? t('city') : t('residence_city');
          const residenceCityQueryKey = getResidenceCityQueryKey({
            residenceCountry,
            url,
          });
          const residenceCityApiKey = Array.isArray(residenceCityQueryKey)
            ? residenceCityQueryKey[0] || ''
            : residenceCityQueryKey;

          return (
            <FormFieldWrapper>
              <Controller
                name={GUEST_FORM_NAMES.residence_city}
                rules={{required}}
                control={control}
                render={({field: {value, ...field}, fieldState: {error}}) => (
                  <PaginatedSearchableSelect
                    variant={VARIANT}
                    label={getRequiredOrOptionalFieldLabel(label, required)}
                    placeholder={`${t('select')}...`}
                    resetKey={`${residenceCountry ?? ''}-${url ?? ''}`}
                    queryKey={
                      Array.isArray(residenceCityQueryKey)
                        ? residenceCityQueryKey
                        : [residenceCityQueryKey]
                    }
                    queryFn={({page, searchQuery}) => {
                      return isSpain
                        ? url
                          ? API.locations.getLocationsByExternalUrl({
                              url,
                              key: residenceCityApiKey,
                              page,
                              searchQuery,
                            })
                          : API.locations.getSpanishResidenceCitiesList({
                              key: residenceCityApiKey,
                              page,
                              searchQuery,
                            })
                        : API.locations.getCitiesList({
                            key: residenceCityApiKey,
                            page,
                            searchQuery,
                            countryCode: residenceCountry || '',
                          });
                    }}
                    disabled={isDisabledField}
                    error={error?.message}
                    value={(value as SelectOption) ?? null}
                    {...field}
                  />
                )}
              />
            </FormFieldWrapper>
          );
        }

        return (
          <FormFieldWrapper>
            <Input
              variant={VARIANT}
              label={getRequiredOrOptionalFieldLabel(t('residence_city'), required)}
              {...register(GUEST_FORM_NAMES.residence_city, {
                required,
                pattern: {
                  value: format,
                  message: t('cant_contain_number_and_symbols'),
                },
                maxLength: {
                  value: maxNamesInputLength,
                  message: t('max_length', {length: maxNamesInputLength}),
                },
              })}
              error={errors[GUEST_FORM_NAMES.residence_city]?.message}
              disabled={isDisabledField}
              autoCorrect="off"
              spellCheck={false}
            />
          </FormFieldWrapper>
        );
      },
      [GUEST_FORM_NAMES.arrived_from_country]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.arrived_from_country}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <CountriesSelect
                variant={VARIANT}
                label={getRequiredOrOptionalFieldLabel(
                  t('arrived_from_country'),
                  required,
                )}
                placeholder={`${t('select')}...`}
                disabled={isDisabledField}
                error={error?.message}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.arrived_from_district]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.arrived_from_district}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <PaginatedSearchableSelect
                variant={VARIANT}
                label={getRequiredOrOptionalFieldLabel(
                  t('arrived_from_district'),
                  required,
                )}
                placeholder={`${t('select')}...`}
                resetKey={housingCountry}
                queryKey={[QUERY_CACHE_KEYS.paginatedDistrictsList, housingCountry]}
                queryFn={({page, searchQuery}) =>
                  API.locations.getDistrictsList({
                    key: QUERY_CACHE_KEYS.paginatedDistrictsList,
                    page,
                    searchQuery,
                    countryCode: housingCountry,
                  })
                }
                disabled={isDisabledField}
                error={error?.message}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.arrived_from_municipality]: ({required, format}) => {
        const isSelectField = getIsSelectFieldType(
          formFields?.[GUEST_FORM_NAMES.arrived_from_municipality]?.type,
        );
        if (isSelectField) {
          return (
            <FormFieldWrapper>
              <Controller
                name={GUEST_FORM_NAMES.arrived_from_municipality}
                rules={{required}}
                control={control}
                render={({field: {value, ...restField}, fieldState: {error}}) => (
                  <PaginatedSearchableSelect
                    variant={VARIANT}
                    label={getRequiredOrOptionalFieldLabel(
                      t('arrived_from_municipality'),
                      required,
                    )}
                    placeholder={`${t('select')}...`}
                    resetKey={arrivedFromDistrict}
                    queryKey={[
                      QUERY_CACHE_KEYS.paginatedMunicipalitiesList,
                      arrivedFromDistrict,
                    ]}
                    queryFn={({page, searchQuery}) =>
                      API.locations.getMunicipalitiesList({
                        key: QUERY_CACHE_KEYS.paginatedMunicipalitiesList,
                        page,
                        searchQuery,
                        countryCode: housingCountry,
                        district: arrivedFromDistrict,
                      })
                    }
                    disabled={isDisabledField}
                    value={(value as SelectOption) ?? null}
                    error={error?.message}
                    {...restField}
                  />
                )}
              />
            </FormFieldWrapper>
          );
        }
        return (
          <FormFieldWrapper>
            <Input
              variant={VARIANT}
              label={getRequiredOrOptionalFieldLabel(
                t('arrived_from_municipality'),
                required,
              )}
              {...register(GUEST_FORM_NAMES.arrived_from_municipality, {
                required,
                pattern: {
                  value: format,
                  message: t('cant_contain_number_and_symbols'),
                },
                maxLength: {
                  value: maxNamesInputLength,
                  message: t('max_length', {length: maxNamesInputLength}),
                },
              })}
              error={errors[GUEST_FORM_NAMES.arrived_from_municipality]?.message}
              disabled={isDisabledField}
              autoCorrect="off"
              spellCheck={false}
              onKeyDown={handleKeyDown(GUEST_FORM_NAMES.arrived_from_municipality)}
            />
          </FormFieldWrapper>
        );
      },
      [GUEST_FORM_NAMES.next_destination_country]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.next_destination_country}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <CountriesSelect
                variant={VARIANT}
                label={getRequiredOrOptionalFieldLabel(
                  t('next_destination_country'),
                  required,
                )}
                placeholder={`${t('select')}...`}
                disabled={isDisabledField}
                error={error?.message}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.next_destination_district]: ({required}) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.next_destination_district}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <PaginatedSearchableSelect
                variant={VARIANT}
                label={getRequiredOrOptionalFieldLabel(
                  t('next_destination_district'),
                  required,
                )}
                placeholder={`${t('select')}...`}
                resetKey={housingCountry}
                queryKey={[QUERY_CACHE_KEYS.paginatedDistrictsList, housingCountry]}
                queryFn={({page, searchQuery}) =>
                  API.locations.getDistrictsList({
                    key: QUERY_CACHE_KEYS.paginatedDistrictsList,
                    page,
                    searchQuery,
                    countryCode: housingCountry,
                  })
                }
                disabled={isDisabledField}
                error={error?.message}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.next_destination_municipality]: ({required, format}) => {
        const showSelectFormat =
          getIsSelectFieldType(
            formFields?.[GUEST_FORM_NAMES.next_destination_municipality]?.type,
          ) && Boolean(nextDestinationDistrict);

        if (showSelectFormat) {
          return (
            <FormFieldWrapper>
              <Controller
                name={GUEST_FORM_NAMES.next_destination_municipality}
                rules={{required}}
                control={control}
                render={({field, fieldState: {error}}) => (
                  <PaginatedSearchableSelect
                    variant={VARIANT}
                    label={getRequiredOrOptionalFieldLabel(
                      t('next_destination_municipality'),
                      required,
                    )}
                    placeholder={`${t('select')}...`}
                    resetKey={nextDestinationDistrict}
                    queryKey={[
                      QUERY_CACHE_KEYS.paginatedMunicipalitiesList,
                      nextDestinationDistrict,
                    ]}
                    queryFn={({page, searchQuery}) =>
                      API.locations.getMunicipalitiesList({
                        key: QUERY_CACHE_KEYS.paginatedMunicipalitiesList,
                        page,
                        searchQuery,
                        countryCode: housingCountry,
                        district: nextDestinationDistrict,
                      })
                    }
                    disabled={isDisabledField}
                    error={error?.message}
                    {...field}
                  />
                )}
              />
            </FormFieldWrapper>
          );
        }
        return (
          <FormFieldWrapper>
            <Input
              variant={VARIANT}
              label={getRequiredOrOptionalFieldLabel(
                t('next_destination_municipality'),
                required,
              )}
              {...register(GUEST_FORM_NAMES.next_destination_municipality, {
                required,
                pattern: {
                  value: format,
                  message: t('cant_contain_number_and_symbols'),
                },
                maxLength: {
                  value: maxNamesInputLength,
                  message: t('max_length', {length: maxNamesInputLength}),
                },
              })}
              error={errors[GUEST_FORM_NAMES.next_destination_municipality]?.message}
              disabled={isDisabledField}
              autoCorrect="off"
              spellCheck={false}
              onKeyDown={handleKeyDown(GUEST_FORM_NAMES.next_destination_municipality)}
            />
          </FormFieldWrapper>
        );
      },
      [GUEST_FORM_NAMES.residence_address]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            variant={VARIANT}
            {...register(GUEST_FORM_NAMES.residence_address, {
              pattern: {
                value: format,
                message: getNameValidationErrorText(housingCountry, policeType),
              },
              required,
            })}
            label={getRequiredOrOptionalFieldLabel(t('residence_address'), required)}
            error={errors[GUEST_FORM_NAMES.residence_address]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.residence_street]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            variant={VARIANT}
            {...register(GUEST_FORM_NAMES.residence_street, {
              pattern: {
                value: format,
                message: getNameValidationErrorText(housingCountry, policeType),
              },
              required,
            })}
            label={getRequiredOrOptionalFieldLabel(t('residence_street'), required)}
            error={errors[GUEST_FORM_NAMES.residence_street]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.residence_house_number]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            variant={VARIANT}
            {...register(GUEST_FORM_NAMES.residence_house_number, {
              pattern: {
                value: format,
                message: getNameValidationErrorText(housingCountry, policeType),
              },
              required,
            })}
            label={getRequiredOrOptionalFieldLabel(t('residence_house_number'), required)}
            error={errors[GUEST_FORM_NAMES.residence_house_number]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.residence_postal_code]: ({required}) => (
        <FormFieldWrapper>
          <Input
            variant={VARIANT}
            label={getRequiredOrOptionalFieldLabel(t('residence_postal_code'), required)}
            {...register(GUEST_FORM_NAMES.residence_postal_code, {
              required,
              validate: validatePostalCode(residenceCountry),
            })}
            error={errors[GUEST_FORM_NAMES.residence_postal_code]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.next_destination_address]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            variant={VARIANT}
            {...register(GUEST_FORM_NAMES.next_destination_address, {
              pattern: {
                value: format,
                message: t('cant_contain_number_and_symbols'),
              },
              required,
            })}
            label={getRequiredOrOptionalFieldLabel(
              t('next_destination_address'),
              required,
            )}
            error={errors[GUEST_FORM_NAMES.next_destination_address]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.fiscal_code]: ({required}) => (
        <FormFieldWrapper>
          <Input
            variant={VARIANT}
            {...register(GUEST_FORM_NAMES.fiscal_code, {
              required,
              pattern: {
                value: PATTERNS.fiscalCode,
                message: t('invalid_fiscal_code'),
              },
            })}
            label={getRequiredOrOptionalFieldLabel(t('fiscal_code'), required)}
            error={errors[GUEST_FORM_NAMES.fiscal_code]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.document_support_number]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            variant={VARIANT}
            {...register(GUEST_FORM_NAMES.document_support_number, {
              required,
              pattern: {
                value: format,
                message: getDocumentSupportNumberInvalidMessage(documentType),
              },
            })}
            label={getRequiredOrOptionalFieldLabel(
              t('document_support_number'),
              required,
            )}
            error={errors[GUEST_FORM_NAMES.document_support_number]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.visa_number]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            variant={VARIANT}
            {...register(GUEST_FORM_NAMES.visa_number, {
              required,
              pattern: {
                value: format,
                message: t('invalid_visa_number.czech'),
              },
            })}
            label={getRequiredOrOptionalFieldLabel(t('visa_number'), required)}
            error={errors[GUEST_FORM_NAMES.visa_number]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.kinship_relationship]: ({
        required,
        options = formFields?.[GUEST_FORM_NAMES.kinship_relationship]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.kinship_relationship}
            rules={{required}}
            control={control}
            render={({field: {value, onChange, name}, fieldState: {error}}) => (
              <Select
                variant={VARIANT}
                label={getRequiredOrOptionalFieldLabel(t('relationship'), required)}
                placeholder={`${t('select')}...`}
                options={(options ?? []) as SelectOption[]}
                value={value as unknown as SelectOption}
                onChange={onChange}
                name={name}
                error={error?.message}
                disabled={isDisabledField}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.external_category_id]: ({
        required,
        options = formFields?.[GUEST_FORM_NAMES.external_category_id]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.external_category_id}
            rules={{required}}
            control={control}
            render={({field: {value, onChange, name}, fieldState: {error}}) => (
              <Select
                variant={VARIANT}
                label={getRequiredOrOptionalFieldLabel(t('category'), required)}
                placeholder={`${t('select')}...`}
                options={(options ?? []) as SelectOption[]}
                value={value as unknown as SelectOption}
                onChange={onChange}
                name={name}
                error={error?.message}
                disabled={isDisabledField}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.invoice_leader_type]: ({
        required,
        options = formFields?.[GUEST_FORM_NAMES.invoice_leader_type]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.invoice_leader_type}
            rules={{required}}
            control={control}
            render={({field: {value, onChange, name}, fieldState: {error}}) => (
              <Select
                variant={VARIANT}
                label={getRequiredOrOptionalFieldLabel(
                  t('invoice_type_needed'),
                  required,
                )}
                placeholder={`${t('select')}...`}
                options={(options ?? []) as SelectOption[]}
                value={value as unknown as SelectOption}
                onChange={onChange}
                name={name}
                error={error?.message}
                disabled={isDisabledField}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.recipient_code_type]: ({
        required,
        options = formFields?.[GUEST_FORM_NAMES.recipient_code_type]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.recipient_code_type}
            rules={{required}}
            control={control}
            render={({field: {value, onChange, name}, fieldState: {error}}) => (
              <Select
                variant={VARIANT}
                label={getRequiredOrOptionalFieldLabel(
                  t('recipient_code_type'),
                  required,
                )}
                placeholder={`${t('select')}...`}
                options={(options ?? []) as SelectOption[]}
                value={value as unknown as SelectOption}
                onChange={onChange}
                name={name}
                error={error?.message}
                disabled={isDisabledField}
              />
            )}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.recipient_code]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            variant={VARIANT}
            label={getRequiredOrOptionalFieldLabel(
              getRecipientCodeLabel(t('recipient_code'), recipientCodeType),
              required,
            )}
            {...register(GUEST_FORM_NAMES.recipient_code, {
              required,
              pattern: {
                value: format,
                message: t('invalid_recipient_code'),
              },
            })}
            error={errors[GUEST_FORM_NAMES.recipient_code]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.vat_number]: ({required, format}) => (
        <FormFieldWrapper>
          <Input
            variant={VARIANT}
            label={getRequiredOrOptionalFieldLabel(t('vat_number'), required)}
            {...register(GUEST_FORM_NAMES.vat_number, {
              required,
              pattern: {
                value: format,
                message: t('invalid_vat_number'),
              },
            })}
            error={errors[GUEST_FORM_NAMES.vat_number]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.chip_card]: ({required}) => (
        <FormFieldWrapper>
          <Input
            variant={VARIANT}
            label={getRequiredOrOptionalFieldLabel(t('chip_card'), required)}
            {...register(GUEST_FORM_NAMES.chip_card, {
              required,
              validate: validateChipCard,
            })}
            error={errors[GUEST_FORM_NAMES.chip_card]?.message}
            disabled={isDisabledField}
            autoCorrect="off"
            spellCheck={false}
            autoCapitalize="none"
          />
        </FormFieldWrapper>
      ),
      [GUEST_FORM_NAMES.mode_of_transport]: ({
        required,
        options = formFields?.[GUEST_FORM_NAMES.mode_of_transport]?.options,
      }) => (
        <FormFieldWrapper>
          <Controller
            name={GUEST_FORM_NAMES.mode_of_transport}
            rules={{required}}
            control={control}
            render={({field: {value, onChange, name}, fieldState: {error}}) => (
              <Select
                variant={VARIANT}
                label={getRequiredOrOptionalFieldLabel(t('mode_of_transport'), required)}
                placeholder={`${t('select')}...`}
                options={(options ?? []) as SelectOption[]}
                value={value as unknown as SelectOption}
                onChange={onChange}
                name={name}
                error={error?.message}
                disabled={isDisabledField}
              />
            )}
          />
        </FormFieldWrapper>
      ),
    }),
    [
      guestId,
      getRequiredOrOptionalFieldLabel,
      formFields,
      t,
      register,
      housingCountry,
      policeType,
      control,
      errors,
      isDisabledField,
      handleKeyDown,
      birthPlaceCountry,
      getValues,
      setHasPhone,
      phoneCodesAsOptions,
      defaultSecondPhoneDetails?.number,
      isGuestSchemaLoading,
      isPoliceActivated,
      documentType,
      guestGroup,
      reservation?.check_in_date,
      shouldFirstGuestBeAdult,
      guestMustBeAdult,
      residenceCountry,
      residenceProvince,
      arrivedFromDistrict,
      nextDestinationDistrict,
      setValue,
      recipientCodeType,
    ],
  );
};
